import React, { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/free-mode";
import { Mousewheel } from "swiper/modules";
import { Volume2, VolumeX } from "lucide-react";
import styles from "./shortsPlayer.module.css";
import { useInfiniteShorts, useLikeShort } from "../../hooks/useGetShorts";
import Loading from "../../common/HousGlass Loader/Loading";

const ShortsPlayer = ({ initialShortId = null }) => {
  const {
    shorts,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteShorts(10);

  const videoRefs = useRef({});
  const [isMuted, setIsMuted] = useState(false);

  let initialSlide = 0;
  if (initialShortId && shorts.length > 0) {
    const index = shorts.findIndex((short) => short.id === initialShortId);
    initialSlide = index >= 0 ? index : 0;
  }


  useEffect(() => {
    if (shorts.length > 0) {
      const activeShort = shorts[initialSlide];
      const videoEl = videoRefs.current[activeShort.id];
      if (videoEl) {
        videoEl.currentTime = 0;
        videoEl.muted = isMuted;
        videoEl.play().catch((e) => console.log("Autoplay prevented:", e));
      }
    }
  }, [shorts, initialSlide, isMuted]);

  if (isLoading && shorts.length === 0) {
    return (
      <div className={styles.shortsLoading}>
        <Loading />
      </div>
    );
  }

  if (error && shorts.length === 0) {
    return <div className={styles.shortsError}>Failed to load shorts</div>;
  }

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  return (
    <Swiper
      direction="vertical"
      spaceBetween={0}
      slidesPerView={1} // Ensures one video per view
      initialSlide={initialSlide}
      speed={600} // Smooth transition speed
      freeMode={false} // Disable free scrolling
      mousewheel={{
        forceToAxis: true, // Scroll only on the Y-axis
        releaseOnEdges: true, // Prevents stuck scrolling
        sensitivity: 1, // Adjust scroll speed
        thresholdDelta: 50, // Prevents accidental small scrolls
      }}
      modules={[Mousewheel]}
      onSlideChange={(swiper) => {
        const activeIndex = swiper.activeIndex;
        const activeShort = shorts[activeIndex];

        Object.entries(videoRefs.current).forEach(([id, videoEl]) => {
          if (id === activeShort.id) {
            videoEl.currentTime = 0;
            videoEl.muted = isMuted;
            videoEl.play().catch((e) => console.log("Play prevented:", e));
          } else {
            videoEl.pause();
          }
        });

        if (swiper.isEnd && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }}
      style={{ height: "100vh" }}
      pagination={{ clickable: true }}
    >
      {shorts.map((short) => (
        <SwiperSlide key={short.id}>
          <div className={styles.shortsItem} data-id={short.id}>
            <div className={styles.videoContainer}>
              <video
                ref={(el) => (videoRefs.current[short.id] = el)}
                src={short.video_url}
                poster={short.thumbnail}
                loop
                playsInline
                muted={isMuted}
                className={styles.shortsVideo}
                preload="metadata"
              />
            </div>
            <div className={styles.shortsInfo}>
              <div className={styles.shortsDetails}>
                <h3 className={styles.shortsTitle}>{short.title}</h3>
                <p className={styles.shortsCreator}>{short.creator}</p>
                <p className={styles.shortsDescription}>{short.description}</p>
              </div>
              <div className={styles.shortsActions}>
                <button
                  className={styles.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMute();
                  }}
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                </button>
               
                
              </div>
            </div>
          </div>
        </SwiperSlide>
      ))}

      {isFetchingNextPage && (
        <SwiperSlide>
          <div className={styles.shortsLoading}>Loading more shorts...</div>
        </SwiperSlide>
      )}
    </Swiper>
  );
};

export default ShortsPlayer;
